import Phaser from "phaser";
import {
  COLORS,
  POWER_PICKUP_AMOUNT,
  RAPID_FIRE_MS,
  SHIELD_DURATION_MS,
  STRENGTH_BOOST_AMOUNT,
} from "../config";
import { AudioFx } from "./AudioFx";
import type PlayerCell from "../entities/PlayerCell";
import type { GameMode, PickupKind } from "../types";

/**
 * Two power-ups, dropped by removed enemies. Their effect depends on the mode:
 *  • engulf: primary = +Strength,  secondary = Shield (next fatal engulf → win).
 *  • shoot:  primary = +power,     secondary = Rapid fire (~10s).
 */
export default class Pickups {
  private scene: Phaser.Scene;
  private player: PlayerCell;
  private mode: GameMode;
  private group: Phaser.Physics.Arcade.Group;

  constructor(scene: Phaser.Scene, player: PlayerCell, mode: GameMode) {
    this.scene = scene;
    this.player = player;
    this.mode = mode;
    this.group = scene.physics.add.group();
    scene.physics.add.overlap(player, this.group, (_p, token) => {
      this.collect(token as Phaser.Physics.Arcade.Sprite);
    });
  }

  spawn(x: number, y: number): void {
    const kind: PickupKind = Math.random() < 0.5 ? "primary" : "secondary";
    const texture =
      kind === "primary" ? "pickup-plus" : this.mode === "engulf" ? "pickup-shield" : "pickup-rapid";
    const token = this.group.create(x, y, texture) as Phaser.Physics.Arcade.Sprite;
    token.setData("kind", kind);
    token.setDepth(8);
    (token.body as Phaser.Physics.Arcade.Body).setCircle(22, 2, 2);

    this.scene.tweens.add({ targets: token, y: y - 10, duration: 700, yoyo: true, repeat: -1, ease: "Sine.easeInOut" });
    this.scene.time.delayedCall(8000, () => {
      if (!token.active) return;
      this.scene.tweens.add({ targets: token, alpha: 0, duration: 1000, onComplete: () => token.destroy() });
    });
  }

  private collect(token: Phaser.Physics.Arcade.Sprite): void {
    if (!token.active) return;
    const kind = token.getData("kind") as PickupKind;
    const now = this.scene.time.now;
    let text: string;
    let color: number;

    if (kind === "primary") {
      color = COLORS.primaryPickup;
      if (this.mode === "engulf") {
        this.player.addStrength(STRENGTH_BOOST_AMOUNT);
        text = `+${STRENGTH_BOOST_AMOUNT} strength`;
      } else {
        this.player.addPower(POWER_PICKUP_AMOUNT);
        text = `+${POWER_PICKUP_AMOUNT} power`;
      }
    } else {
      color = COLORS.secondaryPickup;
      if (this.mode === "engulf") {
        this.player.activateShield(now, SHIELD_DURATION_MS);
        text = "Shield!";
      } else {
        this.player.activateRapidFire(now, RAPID_FIRE_MS);
        text = "Rapid fire!";
      }
    }

    AudioFx.digestChime(2);
    this.floatText(token.x, token.y, text, color);
    const burst = this.scene.add
      .sprite(token.x, token.y, "soft")
      .setTint(color)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(9);
    this.scene.tweens.add({
      targets: burst,
      scale: { from: 0.4, to: 1.6 },
      alpha: { from: 0.8, to: 0 },
      duration: 400,
      onComplete: () => burst.destroy(),
    });
    token.destroy();
  }

  private floatText(x: number, y: number, text: string, color: number): void {
    const t = this.scene.add
      .text(x, y, text, {
        fontFamily: "Arial, sans-serif",
        fontSize: "26px",
        color: Phaser.Display.Color.IntegerToColor(color).rgba,
        fontStyle: "bold",
        stroke: "#0a1020",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(95);
    this.scene.tweens.add({ targets: t, y: y - 60, alpha: 0, duration: 900, ease: "Sine.easeOut", onComplete: () => t.destroy() });
  }
}
